import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { X } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { optionNames, useIssueFields, useRepoMeta } from '@/lib/query'
import { orderOptions } from '@/lib/field-order'
import { parseFilter, serializeFilter, type FilterTerm } from '@/lib/filters'

type Facet = { key: string; label: string; options: string[] }

// Sentinel for the "any value" option — Radix Select can't take an empty value.
const ANY = '__any__'

/**
 * Compose a /q filter from field-backed facets. Reads the current ?filter=,
 * exposes each single-value facet as a dropdown, and rewrites ?filter= on
 * change. Terms it doesn't model (negations, multi-value, label:, free text)
 * are preserved untouched, so editing a facet never silently drops them.
 *
 * There's no State facet: /q evaluates against the open-issue universe, so
 * `is:closed` would always be empty — offering it would mislead.
 */
export function FacetBar({ filter }: { filter: string }) {
  const [, setSearchParams] = useSearchParams()
  const fields = useIssueFields().data
  const repoMeta = useRepoMeta().data

  const facets = useMemo<Facet[]>(
    () =>
      [
        { key: 'priority', label: 'Priority', options: optionNames(fields, 'Priority') },
        { key: 'type', label: 'Type', options: (repoMeta?.issueTypes ?? []).map((t) => t.name) },
        { key: 'stage', label: 'Stage', options: optionNames(fields, 'Stage') },
        { key: 'client', label: 'Client', options: optionNames(fields, 'Client') },
        { key: 'product', label: 'Product', options: optionNames(fields, 'Product') },
        { key: 'workload', label: 'Workload', options: optionNames(fields, 'Workload') },
        { key: 'source', label: 'Source', options: optionNames(fields, 'Source') },
        { key: 'verifier', label: 'Verifier', options: optionNames(fields, 'Verifier') },
        { key: 'assignee', label: 'Assignee', options: (repoMeta?.assignableUsers ?? []).map((u) => u.login) },
      ]
        // Shared field-order rules so facet options match the sidebar and sheet.
        .map((f) => ({ ...f, options: orderOptions(f.key, f.options) }))
        .filter((f) => f.options.length > 0),
    [fields, repoMeta],
  )

  const modeled = useMemo(() => new Set(facets.map((f) => f.key)), [facets])
  const parsed = useMemo(() => parseFilter(filter), [filter])

  // Split parsed terms into the single-value facet selections we can edit and
  // the "rest" carried through verbatim. Duplicate modeled keys keep the first
  // (the dropdown owns the key) and are dropped rather than shadowed into rest.
  const { selected, rest } = useMemo(() => {
    const selected: Record<string, string> = {}
    const rest: FilterTerm[] = []
    for (const t of parsed.terms) {
      if (modeled.has(t.key) && !t.negated && t.values.length === 1) {
        if (!(t.key in selected)) selected[t.key] = t.values[0]!
        continue // first wins; later duplicates are collapsed away
      }
      rest.push(t)
    }
    return { selected, rest }
  }, [parsed, modeled])

  function write(terms: FilterTerm[]) {
    const str = serializeFilter({ terms, text: parsed.text }).trim()
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        if (str) p.set('filter', str)
        else p.delete('filter')
        p.delete('name') // editing facets makes it a custom, unsaved filter
        return p
      },
      { replace: true },
    )
  }

  function setFacet(key: string, value: string | null) {
    const next = { ...selected }
    if (value === null) delete next[key]
    else next[key] = value
    // Drop any non-negated rest term whose key the user is now setting, so a
    // multi-value/duplicate term can't linger and AND against the new choice.
    write([
      ...rest.filter((t) => t.negated || !(t.key in next)),
      ...Object.entries(next).map(([k, v]) => ({ key: k, values: [v], negated: false })),
    ])
  }

  function clearFacets() {
    // Keep negations, free text, and unmodeled terms; drop everything the bar owns.
    write(rest.filter((t) => t.negated || !modeled.has(t.key)))
  }

  const canClear =
    Object.keys(selected).length > 0 || rest.some((t) => !t.negated && modeled.has(t.key))

  return (
    <div className="border-border flex flex-wrap items-center gap-1.5 border-b px-4 py-2">
      <span className="text-faint-foreground mr-1 text-[11px]">Filter</span>
      {facets.map((f) => {
        const value = selected[f.key] ?? ANY
        return (
          <Select key={f.key} value={value} onValueChange={(v) => setFacet(f.key, v === ANY ? null : v)}>
            <SelectTrigger
              size="sm"
              aria-label={f.label}
              className={
                value === ANY
                  ? 'text-faint-foreground h-6 gap-1 border-dashed text-xs'
                  : 'h-6 gap-1 text-xs'
              }
            >
              <SelectValue>
                {value === ANY ? f.label : <span className="font-medium">{value}</span>}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any {f.label.toLowerCase()}</SelectItem>
              {f.options.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      })}
      {canClear && (
        <Button
          variant="ghost"
          size="sm"
          className="text-faint-foreground h-6 px-1.5 text-[11px]"
          onClick={clearFacets}
        >
          <X className="size-3" />
          Clear
        </Button>
      )}
    </div>
  )
}
